import React, { useState } from 'react';
import { Formik, Form, Field, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import {
  TextField,
  Box,
  Typography,
  InputAdornment,
  IconButton,
  FormHelperText,
  CircularProgress,
} from '@mui/material';
import { Visibility, VisibilityOff, VisibilityOutlined } from '@mui/icons-material';
import toast from 'react-hot-toast';
import { FRONTEND_URL } from '../../../../config/env';
import { resetPassword } from '../../../../api/authService';
import { useLocation } from 'react-router-dom';
import PasswordRequirements from '../../../../components/PasswordRequirements';
import { newPasswordYupString } from '../../../../utilities/passwordYup';
import Button from '../../../../components/styled_api/StyledButton';
import AuthSplitLayout from '../AuthSplitLayout';

interface FormValues {
  password: string;
  confirmPassword: string;
}

const validationSchema = Yup.object().shape({
  password: newPasswordYupString('New password is required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password')], "Passwords don't match")
    .required('Please confirm your password'),
});

const ResetPasswordPage: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const location = useLocation();

  const getTokenFromQuery = () => {
    const params = new URLSearchParams(location.search);
    return params.get('token') || '';
  };

  const handleSubmit = async (
    values: FormValues,
    { setSubmitting }: FormikHelpers<FormValues>
  ) => {
    try {
      const token = getTokenFromQuery();
      if (!token) {
        toast.error('Missing or invalid password reset token.');
        setSubmitting(false);
        return;
      }
      const result = await resetPassword(token, values.password);
      if (result.isSuccess) {
        toast.success('Password reset successful!');
        setTimeout(() => {
          window.location.assign(FRONTEND_URL);
        }, 1500);
      } else {
        toast.error(result.message || 'Failed to reset password.');
      }
    } catch {
      toast.error('Failed to reset password.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthSplitLayout>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Box
          aria-hidden
          sx={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            backgroundColor: '#F4C93C',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <VisibilityOutlined sx={{ fontSize: 20, color: '#FCFEFF' }} />
        </Box>
        <Typography
          component="h1"
          variant="h5"
          textAlign="center"
          sx={{ lineHeight: '28px' }}
        >
          Set your new password
        </Typography>
      </Box>

      <Formik
        initialValues={{ password: '', confirmPassword: '' }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({
          errors,
          touched,
          isSubmitting,
          handleChange,
          handleBlur,
          values,
        }) => (
          <Form
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 24,
            }}
          >
            <Box sx={{ width: '100%' }}>
              <Field
                as={TextField}
                fullWidth
                required
                name="password"
                label="New password"
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="new-password"
                error={touched.password && Boolean(errors.password)}
                onChange={handleChange}
                onBlur={handleBlur}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle new password visibility"
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />
              <PasswordRequirements password={values.password} sx={{ mt: 1 }} />
              {touched.password && errors.password ? (
                <FormHelperText error sx={{ mx: 1.75, mt: 0.5 }}>
                  {errors.password}
                </FormHelperText>
              ) : null}
            </Box>

            <Field
              as={TextField}
              fullWidth
              required
              name="confirmPassword"
              label="Repeat new password"
              type={showConfirmPassword ? 'text' : 'password'}
              id="confirmPassword"
              autoComplete="new-password"
              error={
                touched.confirmPassword && Boolean(errors.confirmPassword)
              }
              helperText={
                touched.confirmPassword && errors.confirmPassword
              }
              onChange={handleChange}
              onBlur={handleBlur}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle confirm password visibility"
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        edge="end"
                      >
                        {showConfirmPassword ? (
                          <Visibility />
                        ) : (
                          <VisibilityOff />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />

            <Button
              type="submit"
              className="button__primaryMain"
              width="100%"
              disabled={isSubmitting}
              label={isSubmitting ? '' : 'Save and continue to login'}
              sx={{ '&&': { height: 40, minHeight: 40 } }}
              aria-label="Save and continue to login"
            >
              {isSubmitting && (
                <CircularProgress size={20} color="inherit" />
              )}
            </Button>
          </Form>
        )}
      </Formik>
    </AuthSplitLayout>
  );
};

export default ResetPasswordPage;
